import { useEffect, useRef, useState } from 'react';
import { useScroll } from 'framer-motion';

// Progreso de scroll (0 = arriba del todo, 1 = abajo del todo) a partir del
// cual se considera que el usuario llegó al FINAL de la página. Con solo
// llegar acá NO se libera el cohete todavía: hace falta además que el
// usuario empiece a scrollear hacia arriba (ver más abajo).
export const RELEASE_THRESHOLD = 0.985;

// Progreso de scroll por debajo del cual se considera que el cohete llegó
// arriba del todo (zona del header): ahí se desactiva y vuelve a quedar
// acoplado en la estación, listo para repetirse la próxima vez que se
// llegue de nuevo al final.
const DEACTIVATION_THRESHOLD = 0.06;

// Pequeño margen para no confundir "scrolleó hacia arriba de verdad" con
// micro-variaciones de scroll (rebote elástico en iOS, redondeos, etc).
const UPWARD_EPSILON = 0.0008;

// Fuente de verdad ÚNICA para el estado del cohete, compartida por
// EstacionLanzamiento.js (dibuja el cohete quieto, posado en la plataforma,
// como parte normal del contenido de la página) y Cohete.js (el cohete
// "fixed" que efectivamente vuela hacia el header).
//
// launching:
//  - false -> el cohete "real" es el que está dibujado fijo en la
//    plataforma dentro de EstacionLanzamiento.js. No importa cuánto
//    scrollees dentro de la página, ni siquiera llegar hasta el final y
//    quedarte ahí: se mantiene quieto ahí, esperando.
//  - true  -> se activa recién en el instante en que, HABIENDO llegado ya
//    al final absoluto de la página (RELEASE_THRESHOLD), el usuario
//    empieza a scrollear hacia arriba. En ese instante el cohete estático
//    se oculta y el cohete fixed de Cohete.js -ya posicionado en el mismo
//    lugar exacto- toma el control: a partir de ahí, seguir scrolleando
//    hacia arriba lo mueve normalmente hacia el header. Se mantiene
//    "liberado" mientras subís, y solo vuelve a quedar acoplado en la
//    estación cuando llegás arriba del todo (o hasta la próxima vez que
//    llegues al final y vuelvas a scrollear hacia arriba).
export function useRocketScrollState() {
  const { scrollYProgress } = useScroll();
  const [launching, setLaunching] = useState(false);
  // Se pone en true apenas se toca RELEASE_THRESHOLD; se resetea al llegar
  // arriba del todo. No dispara re-render por sí solo (no es state).
  const reachedEndRef = useRef(false);
  const prevProgressRef = useRef(scrollYProgress.get());

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      const prev = prevProgressRef.current;
      prevProgressRef.current = latest;

      if (latest >= RELEASE_THRESHOLD) {
        reachedEndRef.current = true;
      }

      if (latest <= DEACTIVATION_THRESHOLD) {
        setLaunching(false);
        reachedEndRef.current = false;
        return;
      }

      // Recién liberamos el cohete si ya tocó el final en algún momento Y
      // el usuario está scrolleando hacia arriba de verdad (no solo quieto
      // en el fondo, ni un micro-rebote).
      if (reachedEndRef.current && latest < prev - UPWARD_EPSILON) {
        setLaunching(true);
      }
    });
    return unsubscribe;
  }, [scrollYProgress]);

  return { scrollYProgress, launching };
}